<template>
  <div
    ref="containerRef"
    :class="[
      'tw:relative tw:flex tw:items-center tw:border-b tw:border-[rgba(66,66,66,0.1)]',
      fullWidth ? 'tw:w-full' : '',
      stretch ? 'tw:px-4 tw:gap-0' : '',
      customClass
    ]"
  >
    <slot />
    <div
      ref="indicatorRef"
      class="tw:pointer-events-none tw:absolute! tw:-bottom-px tw:h-0.5 tw:rounded-full tw:bg-usdg-color-text-title tw:opacity-0 tw:transition-all tw:duration-200"
    />
  </div>
</template>

<script setup lang="ts">
import { inject, onBeforeUnmount, onMounted, provide, watch } from 'vue'
import { TabListContextKey, TabsContextKey } from './useTabsContext'

const props = withDefaults(
  defineProps<{
    fullWidth?: boolean
    stretch?: boolean
    customClass?: string
  }>(),
  {
    fullWidth: true,
    stretch: false,
    customClass: ''
  }
)

const tabsContext = inject(TabsContextKey, null)

provide(TabListContextKey, {
  stretch: computed(() => props.stretch)
})

const containerRef = ref<HTMLDivElement | null>(null)
const indicatorRef = ref<HTMLDivElement | null>(null)

const updateIndicator = () => {
  const container = containerRef.value
  const indicator = indicatorRef.value
  if (!container || !indicator) return

  const activeTab = container.querySelector<HTMLButtonElement>('button[data-active-tab="true"]')
  if (!activeTab) {
    indicator.style.opacity = '0'
    return
  }

  const containerRect = container.getBoundingClientRect()
  const tabRect = activeTab.getBoundingClientRect()

  indicator.style.opacity = '1'
  indicator.style.left = `${tabRect.left - containerRect.left}px`
  indicator.style.width = `${tabRect.width}px`
}

let resizeObserver: ResizeObserver | null = null

const observeActiveTab = () => {
  resizeObserver?.disconnect()
  const activeTab = containerRef.value?.querySelector<HTMLButtonElement>(
    'button[data-active-tab="true"]'
  )
  if (!activeTab) return

  resizeObserver = new ResizeObserver(updateIndicator)
  resizeObserver.observe(activeTab)
}

onMounted(() => {
  updateIndicator()
  observeActiveTab()
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

watch(
  () => tabsContext?.value.value,
  () => {
    updateIndicator()
    observeActiveTab()
  },
  { flush: 'post' }
)
</script>
